import type {
    IBaseSolution,
    ICheckResultResponse,
    IPatentImageParams,
    IDetailImage,
    IDisclosureAnalysisData,
    IReportData,
    ISuggestList,
    IOneProfileRequest,
    IExportDocParams,
    IRequestPatentData,
    IExportReportData,
    IReportDetailParams,
    IDetailSummaryParams,
    IDetailSummaryResponse,
    ISrpRequest,
    IResponseContentStorage,
    TRANSLATE_LANGUAGES,
    IPatentGPTSummary,
} from '@/types'
import type { ICoreDataWrapper } from '@eureka/services/src/exception'
import { container } from '@eureka/services';

export function getContentStorageId(data: {
    means: string,
    efficacy: string
}) {
    const url = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/copy`
    return container!.RnDHttp.post<ICoreDataWrapper<string>>({
        url,
        data,
    }).then(res => {
        return res.data
    })
}

export function getResultsList(data: ISrpRequest) {
    return container!.RnDHttp.post<ICoreDataWrapper<ICheckResultResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/srp`,
        data,
    }).then((res) => {
        return res.data as ICheckResultResponse
    })
}

export function getSuggestList(data: string) {
    return container!.RnDHttp.post<ICoreDataWrapper<{
        records: ISuggestList[]
    }>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/patent/query/withPn`,
        data: {
            query: data,
            limit: 10,
        },
    }).then((res) => {
        return res.data.records
    })
}

export function getSolutionProfile<T extends IBaseSolution>(data: IOneProfileRequest) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/srp_one`,
        data,
    }).then(res => {
        return res.data
    })
}

export function exportDoc(params: IExportDocParams): Promise<string> {
    return container!.RnDHttp.post<ICoreDataWrapper<string>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/export`,
        data: {
            ...params,
            is_report: false,
        },
    }).then(res => {
        return res.data
    })
}

export function getPatentDescriptionData<T extends {
    DESC: string
}>(data: IRequestPatentData) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/patent`,
        data: {
            ...data,
            tab: 'desc',
        },
    }).then(res => {
        return res.data
    })
}

export function getPatentClaimData<T extends {
    CLMS: string
}>(data: IRequestPatentData) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/patent`,
        data: {
            ...data,
            tab: 'claims',
        },
    }).then(res => {
        return res.data
    })
}

export function getPatentSummaryData<T extends IDetailSummaryResponse>(data: IDetailSummaryParams) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/patent`,
        data: {
            tab: 'abst',
            ...data,
        },
    }).then(res => {
        return res.data
    });
}

export function getReportData<T extends IReportData>(params: IReportDetailParams) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/report`,
        data: params,
    }).then(res => {
        return res.data
    });
}

export function getPatentImages<T extends IDetailImage>(patentId: string, params: IPatentImageParams) {
    return container!.RnDHttp.get<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/patent/id/${patentId}/full-images`,
        params,
    }).then(res => {
        return res.data
    });
}

export function getGPTSummary(data: { patent_id: string, lang: TRANSLATE_LANGUAGES, trans_lang: TRANSLATE_LANGUAGES }) {
    return container!.RnDHttp.post<ICoreDataWrapper<IPatentGPTSummary>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/summary`,
        data,
    }).then(res => {
        return res.data
    });
}

export function getDisclosureCloudAnalysis<T extends IDisclosureAnalysisData>(companyName: string) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/analysis_cloud`,
        data: {
            q: companyName,
            playbook: 'SmartSearch',
        },
    }).then(res => {
        return res.data
    });
}

export function getDisclosureHeatmapAnalysis<T extends IDisclosureAnalysisData>(companyName: string) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/analysis_efficacy`,
        data: {
            q: companyName,
            playbook: 'SmartSearch',
        },
    }).then(res => {
        return res.data
    });
}

export function exportReport<T extends string>(reportData: IExportReportData) {
    return container!.RnDHttp.post<ICoreDataWrapper<T>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/export`,
        data: reportData,
    }).then(res => {
        return res.data
    });
}

export function getContentFromStorage(cache_id?: string) {
    return container!.RnDHttp.get<ICoreDataWrapper<IResponseContentStorage>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/disclosure/scene/paste`,
        params: { cache_id },
    }).then((res) => {
        return res.data
    })
}
